import { GoalStatus, type GoalTimeline } from "~/../drizzle/schema";
import type { GoalOfLifeRow } from "~/lib/server/goal-of-life";

export type LifeGoal = NonNullable<GoalOfLifeRow["goalOfLife"]>;

export type LifeCategory = GoalOfLifeRow["categoryOfLife"];

/** A goal as the list shows it: the goal, its category and its own steps. */
export type GoalItem = {
  goal: LifeGoal;
  category: LifeCategory;
  steps: { total: number; done: number };
};

export type GoalGroups = {
  /** Categories with an open goal, each with those goals, in the list's order. */
  categories: Array<{ category: LifeCategory; goals: GoalItem[] }>;
  /** Finished goals, which the list sets apart at the bottom. */
  done: GoalItem[];
};

/** All of a person's goals as items; a category without goals comes as a row with no goal. */
export function goalItems(rows: readonly GoalOfLifeRow[]): GoalItem[] {
  return rows.flatMap(({ categoryOfLife, goalOfLife, milestoneData }) =>
    goalOfLife?.id
      ? [
          {
            goal: goalOfLife,
            category: categoryOfLife,
            // Postgres counts arrive as strings.
            steps: {
              total: Number(milestoneData.totalMilestones),
              done: Number(milestoneData.completedMilestones),
            },
          },
        ]
      : [],
  );
}

/** Groups goals under their category, with done goals set apart. Categories without open goals are left out. */
export function groupGoals(items: readonly GoalItem[]): GoalGroups {
  const categories = new Map<string, GoalGroups["categories"][number]>();
  const done: GoalItem[] = [];
  for (const item of items) {
    if (item.goal.status === GoalStatus.COMPLETED) {
      done.push(item);
      continue;
    }
    const group = categories.get(item.category.id);
    if (group) group.goals.push(item);
    else
      categories.set(item.category.id, {
        category: item.category,
        goals: [item],
      });
  }
  return { categories: [...categories.values()], done };
}

/** How many open goals a timeframe chip holds; without a timeframe, every open goal. */
export function countOpenGoals(
  items: readonly GoalItem[],
  timeline?: GoalTimeline,
): number {
  return items.filter(
    (item) =>
      item.goal.status !== GoalStatus.COMPLETED &&
      (!timeline || item.goal.timeline === timeline),
  ).length;
}
